/**
 * `studio.picture_intake` — who may send the studio a consultation-form photo
 * over WhatsApp (phase `whatsapp-picture-intake`, 2026-10-14).
 *
 * When a sender the mode admits sends a photo, the inbound resolver asks a
 * model whether it is a consultation form; a form is stored privately, read,
 * and its answers sent back to the sender to confirm before anything reaches
 * the member's profile. A sender the mode does NOT admit is handled exactly as
 * before the intake existed: the chat agent is told a photo arrived, and
 * nothing is downloaded or stored.
 *
 *   - `off`     — nobody (the default; an absent field behaves as `off`).
 *   - `staff`   — staff only: forms they photograph for a member (and the
 *                 offer to save any other photo to the asset library).
 *   - `members` — members only: their own form.
 *   - `both`    — staff and members.
 *
 * One field with four states rather than two booleans, the shape
 * `trainer_comms_admin_mode` set: the states are one choice, not two.
 *
 * Pure (no I/O) so the L2 parser, the `/config` page and the resolver all read
 * the one vocabulary and the one predicate. A live `studio_config` override
 * applies, so the `/config` Save turns the intake on or off with no deploy.
 */

export const PICTURE_INTAKE_MODES = ["off", "staff", "members", "both"] as const;

export type PictureIntakeMode = (typeof PICTURE_INTAKE_MODES)[number];

/** Who sent the picture, as the resolver knows them. */
export type PictureIntakeSenderKind = "staff" | "member";

export function isPictureIntakeMode(value: unknown): value is PictureIntakeMode {
  return (
    typeof value === "string" &&
    (PICTURE_INTAKE_MODES as ReadonlyArray<string>).includes(value)
  );
}

/**
 * True when `mode` lets this sender's photo into the intake. Absent is `off`:
 * the intake spends a model call per photo and stores what it reads, so it is
 * never on by default.
 */
export function pictureIntakeAdmits(
  mode: PictureIntakeMode | undefined,
  sender: PictureIntakeSenderKind,
): boolean {
  switch (mode) {
    case "both":
      return true;
    case "staff":
      return sender === "staff";
    case "members":
      return sender === "member";
    default:
      return false;
  }
}
